import { useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useI18n } from '../i18n/useT'

/** Auth0 Universal Login names Chinese by region; every other UI locale code
 * is already the tag Auth0 expects. A language the tenant hasn't enabled
 * simply falls back to the tenant default, so passing it is always safe. */
const AUTH0_LOCALE: Record<string, string> = {
  'zh-hans': 'zh-CN',
  'zh-hant': 'zh-TW',
}

export function auth0Locale(uiLocale: string): string {
  return AUTH0_LOCALE[uiLocale] ?? uiLocale
}

interface LoginOptions {
  /** 'signup' opens the registration screen first — for purchase intent,
   * where the visitor most likely has no account yet */
  screenHint?: 'signup' | 'login'
  /** Where the SPA navigates after the Auth0 round trip (default '/') */
  returnTo?: string
}

/** The one way to start sign-in. Carries the app's UI language as
 * `ui_locales`: Auth0 otherwise follows the BROWSER language, so someone who
 * switched the interface to Russian in an English browser would land on an
 * English sign-in page. */
export function useLogin() {
  const { loginWithRedirect } = useAuth0()
  const { locale } = useI18n()

  return useCallback(
    (opts: LoginOptions = {}) =>
      loginWithRedirect({
        appState: opts.returnTo ? { returnTo: opts.returnTo } : undefined,
        authorizationParams: {
          ui_locales: auth0Locale(locale),
          ...(opts.screenHint ? { screen_hint: opts.screenHint } : {}),
        },
      }),
    [loginWithRedirect, locale],
  )
}
